import { useRef, useCallback, useEffect, useState } from 'react';
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk';

import NARRATEICON from "../static/tts_narrate.svg";
import STOPICON from "../static/tts_stop.svg";

// Module-level singletons for cross-instance cleanup (avoids window.* pollution)
let _azureSynthesizer = null;
let _azureAudioSource = null;
let _azureAudioContext = null;

// CSS class constants for TTS icon states
const TTS_CLASS = {
  ICON: 'tts-icon',
  LOADING: 'tts-loading',
  PLAYING: 'tts-playing',
};

/**
 * Custom hook to handle Text-to-Speech using Azure Cognitive Services
 * with a fallback to native browser synthesis.
 *
 * Icon states:
 *   narrate  → tts_narrate.svg (idle, ready to play)
 *   loading  → tts_narrate.svg + .tts-loading CSS class (pulsing animation)
 *   playing  → tts_stop.svg + .tts-playing CSS class (click to stop)
 *
 * @param {string} token - Azure Speech Service API Token.
 * @param {string} region - Azure Speech Service Region (e.g., "eastus").
 * @returns {Object} An object containing the textToSpeech function.
 */
export const useTextToSpeech = (token, region) => {
  const synthesizerRef = useRef(null);
  const audioContextRef = useRef(null);
  const audioSourceRef = useRef(null);
  const nativeUtteranceRef = useRef(null);
  const isMountedRef = useRef(true);
  const generationRef = useRef(0);
  const [ttsState, setTtsState] = useState('idle'); // 'idle' | 'loading' | 'playing'

  const safeSetTtsState = (state) => {
    if (isMountedRef.current) setTtsState(state);
  };

  /**
   * Detects whether the input text is predominantly Spanish or English.
   * @param {string} text - The text content to analyze.
   * @returns {"es"|"en"} The detected language code.
   */
  const detectLanguage = (text) => {
    if (!text) return "en";

    const words = text.split(/\s+/).filter(Boolean);
    const spanishWords = [
      "el","la","de","y","un","una","que","por","para","los","las","en","con",
      "muy","bien","gracias","hola","como","soy","estoy","tengo","buenos",
      "dias","noche","señor","señora","usted"
    ];

    let spanishScore = 0;
    let englishScore = 0;

    for (const word of words) {
      const clean = word.toLowerCase().replace(/[^a-záéíóúñ¿¡]/g, "");
      if (/[áéíóúñ¿¡]/.test(clean)) spanishScore += 2;
      else if (spanishWords.includes(clean)) spanishScore += 1.5;
      else if (/^[a-z]+$/.test(clean)) englishScore += 1;
    }

    const total = spanishScore + englishScore || 1;
    return spanishScore / total > 0.4 ? "es" : "en";
  };

  /**
   * Sets icon to LOADING state (narrate icon + pulse animation).
   * @param {HTMLImageElement} icon - The DOM element of the icon.
   */
  const setLoadingUI = (icon) => {
    if (icon) {
      icon.src = NARRATEICON;
      icon.title = 'Starting narration...';
      icon.classList.add(TTS_CLASS.LOADING);
      icon.classList.remove(TTS_CLASS.PLAYING);
    }
    safeSetTtsState('loading');
  };

  /**
   * Sets icon to PLAYING state (stop icon, click to stop).
   * @param {HTMLImageElement} icon - The DOM element of the icon.
   */
  const setPlayingUI = (icon) => {
    if (icon) {
      icon.src = STOPICON;
      icon.title = 'Stop narration';
      icon.classList.remove(TTS_CLASS.LOADING);
      icon.classList.add(TTS_CLASS.PLAYING);
    }
    safeSetTtsState('playing');
  };

  /**
   * Resets icon to IDLE/NARRATE state.
   * @param {HTMLImageElement} icon - The DOM element of the icon.
   */
  const setIdleUI = (icon) => {
    if (icon) {
      icon.src = NARRATEICON;
      icon.title = 'Narrate';
      icon.classList.remove(TTS_CLASS.LOADING, TTS_CLASS.PLAYING);
    }
    safeSetTtsState('idle');
  };

  /**
   * Stops all currently playing audio from both Azure and Native sources.
   * Cleans up global window objects and resets all active TTS icons.
   */
  const stopAllSpeech = useCallback(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }

    if (_azureSynthesizer) {
      try { _azureSynthesizer.close(); } catch(e){}
      _azureSynthesizer = null;
    }
    if (_azureAudioSource) {
      try { _azureAudioSource.stop(); } catch(e){}
      _azureAudioSource = null;
    }
    if (_azureAudioContext) {
      try { _azureAudioContext.close(); } catch(e){}
      _azureAudioContext = null;
    }

    synthesizerRef.current = null;
    audioSourceRef.current = null;
    audioContextRef.current = null;
    nativeUtteranceRef.current = null;

    // Reset all active TTS icons (loading or playing) back to idle
    const activeIcons = document.querySelectorAll(
      `.${TTS_CLASS.LOADING}, .${TTS_CLASS.PLAYING}`
    );
    activeIcons.forEach(icon => {
      icon.src = NARRATEICON;
      icon.title = 'Narrate';
      icon.classList.remove(TTS_CLASS.LOADING, TTS_CLASS.PLAYING);
    });

    safeSetTtsState('idle');
  }, []);

  /**
   * Fallback method using the browser's native SpeechSynthesis API.
   * @param {string} text - The text to speak.
   * @param {"es"|"en"} lang - The language code.
   * @param {HTMLImageElement} iconElement - The UI element to update.
   */
  const nativeSpeak = (text, lang, iconElement) => {
    if (!("speechSynthesis" in window)) {
      setIdleUI(iconElement);
      return;
    }

    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = lang === "es" ? "es-ES" : "en-US";

    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v =>
      lang === "es" ? v.lang.startsWith("es") : v.lang.startsWith("en")
    );

    if (preferredVoice) utterance.voice = preferredVoice;

    utterance.onstart = () => setPlayingUI(iconElement);
    utterance.onend = () => stopAllSpeech();
    utterance.onerror = () => stopAllSpeech();

    nativeUtteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  };

  /**
   * Main handler to trigger text-to-speech.
   * Can accept a string directly or an HTML element to parse text from.
   * @param {string|HTMLElement} inputSource - The text string or DOM element containing text.
   * @param {HTMLImageElement} [optionalIconElement=null] - The icon element if inputSource is a string.
   */
  const textToSpeech = useCallback((inputSource, optionalIconElement = null) => {
    let contentToSpeak = "";
    let iconElement = null;

    if (typeof inputSource === "string") {
      contentToSpeak = inputSource;
      iconElement = optionalIconElement;
    } else if (inputSource && inputSource instanceof Element) {
      iconElement = inputSource;
      const parent = iconElement.closest('.assistant_message');
      const adaptiveCard = parent?.querySelector('.teams_adaptive_card');

      if (parent) {
         const clone = parent.cloneNode(true);
         clone.querySelectorAll('.copy_icon, .tts-icon').forEach(el => el.remove());
         contentToSpeak = adaptiveCard?.innerText || clone.innerText || clone.textContent || "";
      }
    }

    if (!contentToSpeak.trim()) {
      return;
    }

    // Check if clicking an already-active icon (loading or playing) → stop
    const isActive = iconElement && (
      iconElement.classList.contains(TTS_CLASS.PLAYING) ||
      iconElement.classList.contains(TTS_CLASS.LOADING)
    );

    stopAllSpeech();

    if (isActive) {
      return;
    }

    // Increment generation to invalidate stale async callbacks
    const thisGeneration = ++generationRef.current;

    // Show loading state immediately
    setLoadingUI(iconElement);

    const lang = detectLanguage(contentToSpeak);

    if (!token || !region || !SpeechSDK ) {
      nativeSpeak(contentToSpeak, lang, iconElement);
      return;
    }

    try {
      const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(token, region);
      speechConfig.speechSynthesisVoiceName =
        lang === "es" ? "es-ES-ElviraNeural" : "en-US-JennyNeural";

      const stream = SpeechSDK.AudioOutputStream.createPullStream();
      const audioConfig = SpeechSDK.AudioConfig.fromStreamOutput(stream);
      const synthesizer = new SpeechSDK.SpeechSynthesizer(speechConfig, audioConfig);

      synthesizerRef.current = synthesizer;
      _azureSynthesizer = synthesizer;

      synthesizer.speakTextAsync(
        contentToSpeak,
        result => {
          if (generationRef.current !== thisGeneration) return;

          if (result.reason === SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();

            audioContextRef.current = audioCtx;
            _azureAudioContext = audioCtx;

            const audioData = new Uint8Array(result.audioData);
            audioCtx.decodeAudioData(audioData.buffer, buffer => {
              if (generationRef.current !== thisGeneration) return;

              const source = audioCtx.createBufferSource();
              source.buffer = buffer;
              source.connect(audioCtx.destination);

              audioSourceRef.current = source;
              _azureAudioSource = source;

              // Transition: loading → playing
              setPlayingUI(iconElement);

              source.onended = () => {
                stopAllSpeech();
              };

              source.start(0);
            }, () => {
              // decodeAudioData failed — fall back to native speech
              if (generationRef.current !== thisGeneration) return;
              nativeSpeak(contentToSpeak, lang, iconElement);
            });
          } else {
            nativeSpeak(contentToSpeak, lang, iconElement);
          }

          synthesizer.close();
          synthesizerRef.current = null;
          _azureSynthesizer = null;
        },
        error => {
          if (generationRef.current !== thisGeneration) return;
          nativeSpeak(contentToSpeak, lang, iconElement);
        }
      );
    } catch (err) {
      nativeSpeak(contentToSpeak, lang, iconElement);
    }
  }, [token, region, stopAllSpeech]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      stopAllSpeech();
    };
  }, [stopAllSpeech]);

  return { textToSpeech, ttsState, stopSpeech: stopAllSpeech };
};
